import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtMoney } from "../components/ops/opsUtils";
import { Building2, Plus, Users, Search, Eye, Loader2, Sparkles, RefreshCw, ExternalLink, GitMerge, Trash2, Archive, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MergeFirmModal from "../components/admin/MergeFirmModal";
import { CreateFirmDialog } from "../components/admin/CreateFirmDialog";
import { LegacyImportButton } from "../components/admin/LegacyImportButton";
import { toast } from "sonner";

// Open a new tab into the impersonated firm portal. Uses admin-direct-bypass:
// the URL hash carries the firm_id only — no backend impersonate call needed —
// and the axios interceptor injects the admin token + X-Impersonate-Firm header
// on every /portal/* call.
function startImpersonation(firmId, userEmail) {
  const url = userEmail
    ? `/portal/impersonate#firm=${encodeURIComponent(firmId)}&user=${encodeURIComponent(userEmail)}`
    : `/portal/impersonate#firm=${encodeURIComponent(firmId)}`;
  window.open(url, "_blank");
  return Promise.resolve();
}

export default function FirmsIndex() {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [resetting, setResetting] = useState(false);
  const [view, setView] = useState("active"); // active | archived
  const [mergeSource, setMergeSource] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/operations/firms`, { params: view === "archived" ? { deleted_only: true } : {} });
      setFirms(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const softDeleteFirm = async (firm) => {
    const force = firm.active_loan_count > 0;
    const msg = force
      ? `Firm ${firm.name} has ${firm.active_loan_count} active loans. Force-archive anyway?\n\nIt will be hidden from default lists but loans remain. Reassign or merge first when possible.`
      : `Archive firm ${firm.name}?\n\nIt will be hidden from default lists but recoverable from the Archived view.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/operations/firms/${firm.firm_id}${force ? "?force=true" : ""}`);
      toast.success("Firm archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restoreFirm = async (firm) => {
    try {
      await api.post(`/operations/firms/${firm.firm_id}/restore`);
      toast.success("Firm restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetDemo = async () => {
    if (!window.confirm("Wipe and reseed the demo sandbox? Production data is untouched (the demo_only flag is the boundary).")) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/demo/reset`);
      toast.success(`Demo reset · ${data.seeded?.loans || 8} loans seeded`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetting(false); }
  };

  const filtered = firms.filter((f) =>
    !q.trim() || f.name.toLowerCase().includes(q.toLowerCase()) ||
    (f.domains || []).some((d) => d.includes(q.toLowerCase()))
  );

  const totalLoans = firms.reduce((s, f) => s + f.loan_count, 0);
  const totalActive = firms.reduce((s, f) => s + f.active_loan_count, 0);
  const totalPrincipal = firms.reduce((s, f) => s + f.total_principal, 0);

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto" data-testid="firms-index">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <Building2 className="w-3.5 h-3.5" /> Firms
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Law firms</h1>
          <p className="text-sm text-[#6B7280] mt-1">{firms.length} firms · {totalActive} active loans · {fmtMoney(totalPrincipal)} principal</p>
        </div>
      </div>

      {/* Internal-only demo + impersonation controls */}
      <div className="mb-5 bg-white border border-[#FDE68A] rounded-xl p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3" data-testid="demo-controls">
        <div className="flex items-center gap-2 text-xs text-[#92400E]">
          <Sparkles className="w-3.5 h-3.5" />
          <span className="font-medium">Internal tools</span>
          <span className="text-[#A16207]">— click "View portal" on any firm row to impersonate. Demo links are unauthenticated.</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href="/demo/admin" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] hover:border-[#0066FF] hover:text-[#0066FF] text-[#374151] text-xs font-medium" data-testid="open-demo-admin">
            <ExternalLink className="w-3.5 h-3.5" /> Demo admin
          </a>
          <a href="/demo/attorney" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#E5E7EB] hover:border-[#0066FF] hover:text-[#0066FF] text-[#374151] text-xs font-medium" data-testid="open-demo-attorney">
            <ExternalLink className="w-3.5 h-3.5" /> Demo attorney
          </a>
          <button onClick={resetDemo} disabled={resetting} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#A16207] hover:bg-[#92400E] text-white text-xs font-medium disabled:opacity-60" data-testid="reset-demo-btn">
            {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Reset demo sandbox
          </button>
          <LegacyImportButton />
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="h-8 px-3 bg-[#0066FF] hover:bg-[#0052CC] text-white text-xs font-medium"
            data-testid="create-firm-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New firm
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search firms or domains…" className="pl-9 h-9" data-testid="firms-search" />
          </div>
          <div className="inline-flex bg-[#F3F4F6] rounded-md p-0.5" data-testid="firms-view-toggle">
            <button onClick={() => setView("active")} data-testid="firms-view-active" className={`px-3 h-8 text-xs font-medium rounded ${view === "active" ? "bg-white text-[#0A2540] shadow-sm" : "text-[#6B7280]"}`}>Active</button>
            <button onClick={() => setView("archived")} data-testid="firms-view-archived" className={`px-3 h-8 text-xs font-medium rounded inline-flex items-center gap-1 ${view === "archived" ? "bg-white text-[#0A2540] shadow-sm" : "text-[#6B7280]"}`}><Archive className="w-3.5 h-3.5" /> Archived</button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <th className="px-4 py-2.5 font-medium">Firm</th>
              <th className="px-4 py-2.5 font-medium">Domains</th>
              <th className="px-4 py-2.5 font-medium text-right">Users</th>
              <th className="px-4 py-2.5 font-medium text-right">Active loans</th>
              <th className="px-4 py-2.5 font-medium text-right">Total loans</th>
              <th className="px-4 py-2.5 font-medium text-right">Principal</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6B7280]" data-testid="firms-loading">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-16 text-center" data-testid="firms-empty">
                <div className="inline-flex flex-col items-center text-[#6B7280]">
                  <div className="w-12 h-12 rounded-full bg-[#F0F6FF] flex items-center justify-center mb-3"><Building2 className="w-5 h-5 text-[#0066FF]" /></div>
                  <div className="text-sm font-medium text-[#0A2540]">{view === "archived" ? "Nothing in the archive." : "No firms yet"}</div>
                </div>
              </td></tr>
            ) : filtered.map((f) => (
              <tr key={f.firm_id} className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] ${view === "archived" ? "opacity-70" : ""}`} data-testid={`firm-row-${f.firm_id}`}>
                <td className="px-4 py-3"><Link to={`/admin/firms/${f.firm_id}`} className="text-[#0066FF] hover:underline font-medium">{f.name}</Link>{f.is_deleted ? <span className="ml-2 text-[11px] text-[#B91C1C]">· archived</span> : null}</td>
                <td className="px-4 py-3 text-xs text-[#6B7280]">{(f.domains || []).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums">{f.user_count}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#0066FF] font-medium">{f.active_loan_count}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#6B7280]">{f.loan_count}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[#0A2540] font-medium">{fmtMoney(f.total_principal)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    {view !== "archived" && (
                      <>
                        <button
                          onClick={() => startImpersonation(f.firm_id).catch((e) => toast.error(formatApiError(e)))}
                          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-[#E5E7EB] hover:border-[#A16207] hover:text-[#A16207] text-[#374151] text-xs font-medium transition-colors"
                          data-testid={`impersonate-${f.firm_id}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> Portal
                        </button>
                        <button
                          onClick={() => setMergeSource(f)}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-[#E5E7EB] hover:border-[#0066FF] hover:text-[#0066FF] text-[#374151] transition-colors"
                          title="Merge into another firm"
                          data-testid={`merge-${f.firm_id}`}
                        >
                          <GitMerge className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => softDeleteFirm(f)}
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-[#E5E7EB] hover:border-[#B91C1C] hover:text-[#B91C1C] text-[#9CA3AF] transition-colors"
                          title="Archive (soft-delete)"
                          data-testid={`delete-${f.firm_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {view === "archived" && (
                      <button
                        onClick={() => restoreFirm(f)}
                        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-[#A7F3D0] bg-[#ECFDF5] hover:bg-[#D1FAE5] text-[#047857] text-xs font-medium transition-colors"
                        data-testid={`restore-${f.firm_id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Restore
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mergeSource && <MergeFirmModal source={mergeSource} onClose={() => setMergeSource(null)} onMerged={() => { setMergeSource(null); load(); }} />}
      <CreateFirmDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={() => load()} />
    </div>
  );
}
